/**
 * `/shortform` — 숏폼 (긴 영상 → AI 하이라이트)
 * 핸드오프: docs/handoff/video-editor/project/v3_6/v3-6-short.jsx
 *
 * 레이아웃:
 *  • Source Picker (기존 긴 영상 선택)
 *  • Source Player + HighlightTimeline (파형 + AI 하이라이트 pill)
 *  • Clips Grid (9:16 카드)
 *  • Detail Panel (점수·캡션·해시태그·게시)
 *  • Bottom CommandDock (URL · 업로드 · 옵션)
 */
'use client'

import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import { V3Icon, type V3IconName } from '@/components/icons/V3Icon'
import { useMakerStore } from '@/store/makerStore'

type Tone = 'violet' | 'cyan' | 'lime' | 'coral' | 'rose' | 'amber' | 'ink'
type Platform = 'reels' | 'tiktok' | 'yt'

interface Highlight {
  start: number
  dur: number
  score: number
  label: string
}
interface Source {
  id: string
  title: string
  icon: V3IconName
  tone: Tone
  dur: number
  highlights: Highlight[]
}
interface ShortClip {
  id: string
  title: string
  tone: Tone
  dur: number
  from: number
  score: number
  platform: Platform
  caption: string
  snsCaption: string
  hashtags: string[]
  signals: Array<{ label: string; value: number }>
}

const SOURCES: Source[] = [
  {
    id: 'src1',
    title: '팟캐스트 EP.07 — 디자이너의 하루',
    icon: 'mic',
    tone: 'violet',
    dur: 1842,
    highlights: [
      { start: 32, dur: 28, score: 92, label: '후크' },
      { start: 184, dur: 36, score: 88, label: '에피소드' },
      { start: 412, dur: 42, score: 95, label: '핵심 인사이트' },
      { start: 720, dur: 28, score: 81, label: 'Q&A' },
      { start: 1080, dur: 32, score: 90, label: '데모' },
      { start: 1420, dur: 38, score: 86, label: '마무리' },
    ],
  },
  {
    id: 'src2',
    title: '강의 — React Hooks 입문',
    icon: 'film',
    tone: 'cyan',
    dur: 2964,
    highlights: [
      { start: 60, dur: 24, score: 84, label: 'intro' },
      { start: 540, dur: 30, score: 92, label: 'useState' },
      { start: 1240, dur: 26, score: 89, label: 'useEffect' },
      { start: 2100, dur: 32, score: 78, label: 'Q&A' },
    ],
  },
  {
    id: 'src3',
    title: '행사 키노트 — Maxform Day 2026',
    icon: 'trend',
    tone: 'rose',
    dur: 2412,
    highlights: [
      { start: 120, dur: 28, score: 88, label: '오프닝' },
      { start: 760, dur: 36, score: 94, label: '신기능' },
      { start: 1480, dur: 32, score: 86, label: '사용 사례' },
      { start: 2120, dur: 30, score: 90, label: '다음 단계' },
    ],
  },
]

const SHORTS: ShortClip[] = [
  {
    id: 's1',
    title: '후크 — 디자이너의 진짜 일상',
    tone: 'violet',
    dur: 28,
    from: 32,
    score: 92,
    platform: 'reels',
    caption: '디자이너의\n진짜 일상은…',
    snsCaption:
      '디자인 일은 화려할 거 같지만, 실제로 회의가 절반이에요. 그래도 이 직업을 사랑하는 이유는…',
    hashtags: ['디자이너', '직장인브이로그', '오피스라이프', '디자인일상'],
    signals: [
      { label: '감정 강도', value: 92 },
      { label: '음성 명료도', value: 88 },
      { label: '비주얼 매력', value: 76 },
    ],
  },
  {
    id: 's2',
    title: '핵심 인사이트 · 디자인의 본질',
    tone: 'rose',
    dur: 42,
    from: 412,
    score: 95,
    platform: 'reels',
    caption: '좋은 디자인은\n질문에서 시작된다',
    snsCaption: '10년 동안 배운 단 하나의 교훈. "왜?"를 5번 물으면 진짜 문제가 보입니다.',
    hashtags: ['UX', '디자인사고', '프로덕트디자인', '5whys'],
    signals: [
      { label: '감정 강도', value: 96 },
      { label: '음성 명료도', value: 94 },
      { label: '비주얼 매력', value: 88 },
    ],
  },
  {
    id: 's3',
    title: '에피소드 — 첫 번째 실패',
    tone: 'amber',
    dur: 36,
    from: 184,
    score: 88,
    platform: 'tiktok',
    caption: '내 첫 번째\n실패한 디자인',
    snsCaption: '처음으로 출시한 앱이 1주일 만에 망한 이야기. 그때 배운 5가지를 풀어봅니다.',
    hashtags: ['실패담', '스타트업', '디자인히스토리'],
    signals: [
      { label: '감정 강도', value: 90 },
      { label: '음성 명료도', value: 86 },
      { label: '비주얼 매력', value: 78 },
    ],
  },
  {
    id: 's4',
    title: '데모 — 라이브 와이어프레임',
    tone: 'lime',
    dur: 32,
    from: 1080,
    score: 90,
    platform: 'yt',
    caption: '5분 만에\n와이어프레임 짜기',
    snsCaption: '실시간으로 스케치부터 클릭 가능한 프로토타입까지. 도구 없이 종이만으로!',
    hashtags: ['와이어프레임', '튜토리얼', '디자인툴'],
    signals: [
      { label: '감정 강도', value: 84 },
      { label: '음성 명료도', value: 92 },
      { label: '비주얼 매력', value: 94 },
    ],
  },
  {
    id: 's5',
    title: 'Q&A — 포트폴리오 조언',
    tone: 'cyan',
    dur: 28,
    from: 720,
    score: 81,
    platform: 'tiktok',
    caption: '주니어 포폴\n이건 빼세요',
    snsCaption: '신입 면접관이 가장 싫어하는 포트폴리오 3가지. 1번이 의외라는 평이…',
    hashtags: ['포트폴리오', '주니어디자이너', '취업준비'],
    signals: [
      { label: '감정 강도', value: 78 },
      { label: '음성 명료도', value: 86 },
      { label: '비주얼 매력', value: 72 },
    ],
  },
  {
    id: 's6',
    title: '마무리 — 한 마디',
    tone: 'coral',
    dur: 38,
    from: 1420,
    score: 86,
    platform: 'reels',
    caption: '10년 차가\n남기는 한 마디',
    snsCaption: '그래도 이 일을 계속 하는 이유. 매일 누군가의 화면을 더 낫게 만든다는 것.',
    hashtags: ['디자인', '10년차', '동기부여'],
    signals: [
      { label: '감정 강도', value: 88 },
      { label: '음성 명료도', value: 90 },
      { label: '비주얼 매력', value: 80 },
    ],
  },
  {
    id: 's7',
    title: '보너스 — 도구 추천 3종',
    tone: 'ink',
    dur: 24,
    from: 920,
    score: 79,
    platform: 'yt',
    caption: '꼭 써야 할\n디자인 도구 3개',
    snsCaption: '시간을 아껴주는 도구만 추렸어요. 1번은 무료고 2·3번은 학생 할인이…',
    hashtags: ['디자인툴', '워크플로우', '생산성'],
    signals: [
      { label: '감정 강도', value: 72 },
      { label: '음성 명료도', value: 84 },
      { label: '비주얼 매력', value: 86 },
    ],
  },
  {
    id: 's8',
    title: '팁 — 색 고르는 법',
    tone: 'violet',
    dur: 30,
    from: 600,
    score: 83,
    platform: 'reels',
    caption: '1분 만에\n색 잘 고르는 법',
    snsCaption: 'OKLCH로 lightness만 맞추세요. 나머지는 hue 한 줄만 바꾸면 끝.',
    hashtags: ['컬러팔레트', 'OKLCH', 'UI팁'],
    signals: [
      { label: '감정 강도', value: 76 },
      { label: '음성 명료도', value: 88 },
      { label: '비주얼 매력', value: 90 },
    ],
  },
]

const PLATFORM_DOT: Record<Platform, string> = {
  reels: '#FF6FA8',
  tiktok: '#00D4E0',
  yt: '#FF6B5B',
}

const fmtClock = (s: number) => {
  const m = Math.floor(s / 60)
  const sec = Math.floor(s % 60)
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
}
const fmtMin = (s: number) => {
  const m = Math.floor(s / 60)
  const sec = Math.floor(s % 60)
  if (m >= 60) {
    const h = Math.floor(m / 60)
    return `${h}:${String(m % 60).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
  }
  return `${m}:${String(sec).padStart(2, '0')}`
}

export default function ShortformPage() {
  const [src, setSrc] = useState<Source>(SOURCES[0])
  const [selected, setSelected] = useState<string>('s2')
  const [filter, setFilter] = useState<'all' | Platform>('all')
  const [playing, setPlaying] = useState(false)
  const [head, setHead] = useState(34.2)
  const router = useRouter()

  // playhead 시뮬레이션
  useEffect(() => {
    if (!playing) return
    const id = setInterval(() => setHead((p) => (p + 0.4 > src.dur ? 0 : p + 0.4)), 100)
    return () => clearInterval(id)
  }, [playing, src.dur])

  const visibleClips = filter === 'all' ? SHORTS : SHORTS.filter((c) => c.platform === filter)
  const sel = SHORTS.find((c) => c.id === selected) ?? null

  return (
    <main
      className="v36-fade-in"
      style={{ maxWidth: 1400, margin: '0 auto', padding: '24px 16px 160px' }}
    >
      {/* HEADER */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 24,
          flexWrap: 'wrap',
        }}
      >
        <button
          className="v36-btn v36-btn--ghost v36-btn--sm"
          onClick={() => router.push('/')}
          style={{ padding: '0 10px 0 8px' }}
        >
          <V3Icon name="arrowL" size={14} />
          <span>홈</span>
        </button>
        <span style={{ color: 'var(--v36-ink-5)' }}>/</span>
        <span className="v36-label" style={{ color: 'var(--v36-ink-2)' }}>
          숏폼
        </span>
        <span style={{ flex: 1 }} />
        <div
          className="v36-pc-only"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '4px 12px',
            borderRadius: 999,
            background: 'rgba(124,92,255,0.10)',
            border: '1px solid rgba(124,92,255,0.25)',
          }}
        >
          <span className="v36-live-dot" />
          <span
            className="v36-mono"
            style={{
              fontSize: 10,
              color: 'var(--v36-ink-2)',
              textTransform: 'uppercase',
              letterSpacing: '0.12em',
            }}
          >
            AI · 12개 하이라이트 검출됨
          </span>
        </div>
      </div>

      {/* HERO TITLE */}
      <div style={{ marginBottom: 28 }}>
        <div className="v36-label" style={{ marginBottom: 12 }}>
          SHORTFORM · LONG → SHORT
        </div>
        <h1 className="v36-h1" style={{ margin: 0, marginBottom: 8 }}>
          긴 영상에서{' '}
          <span
            style={{
              background: 'linear-gradient(90deg, #7C5CFF, #FF6FA8)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            핵심만
          </span>{' '}
          골라드려요
        </h1>
        <p style={{ margin: 0, color: 'var(--v36-ink-3)', fontSize: 14 }}>
          AI가 말의 강세·표정·BGM 변화를 분석해 숏폼 후보를 자동 추출했습니다.
        </p>
      </div>

      {/* BODY GRID */}
      <div
        className="v36-short-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.4fr) 320px',
          gap: 16,
        }}
      >
        {/* LEFT */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
          {/* Source picker */}
          <div
            className="v36-glass"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 12px',
              borderRadius: 12,
            }}
          >
            <V3Icon name="film" size={14} style={{ color: 'var(--v36-violet)' }} />
            <span className="v36-label" style={{ fontSize: 9 }}>
              SOURCE
            </span>
            <span style={{ width: 1, height: 16, background: 'var(--v36-line)' }} />
            <div
              className="v36-scroll"
              style={{ display: 'flex', gap: 6, overflowX: 'auto', flex: 1 }}
            >
              {SOURCES.map((s) => (
                <button
                  key={s.id}
                  onClick={() => {
                    setSrc(s)
                    setHead(0)
                  }}
                  className={`v36-chip ${src.id === s.id ? 'v36-chip--active' : ''}`}
                  style={{ height: 28, fontSize: 12, flexShrink: 0 }}
                >
                  <V3Icon name={s.icon} size={11} />
                  <span
                    style={{
                      maxWidth: 160,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {s.title}
                  </span>
                  <span
                    className="v36-mono"
                    style={{ marginLeft: 4, opacity: 0.6, fontSize: 10 }}
                  >
                    {fmtMin(s.dur)}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Source player + waveform */}
          <div className="v36-glass" style={{ padding: 14, borderRadius: 16 }}>
            <div
              className={`v36-thumb v36-thumb--${src.tone}`}
              style={{
                width: '100%',
                aspectRatio: '16/9',
                borderRadius: 12,
                marginBottom: 12,
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  inset: 16,
                  border: '1px dashed rgba(255,255,255,0.18)',
                  borderRadius: 6,
                  pointerEvents: 'none',
                }}
              />
              <button
                onClick={() => setPlaying(!playing)}
                aria-label={playing ? '일시정지' : '재생'}
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: 72,
                  height: 72,
                  borderRadius: '50%',
                  background: 'rgba(0,0,0,0.55)',
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  border: '1px solid rgba(255,255,255,0.4)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <V3Icon name={playing ? 'pause' : 'play'} size={26} fill="#fff" />
              </button>
              <div
                className="v36-mono"
                style={{
                  position: 'absolute',
                  left: 14,
                  top: 12,
                  padding: '4px 8px',
                  borderRadius: 4,
                  background: 'rgba(0,0,0,0.6)',
                  color: '#fff',
                  fontSize: 11,
                }}
              >
                {fmtClock(head)}
              </div>
              <div
                className="v36-mono"
                style={{
                  position: 'absolute',
                  right: 14,
                  top: 12,
                  padding: '4px 8px',
                  borderRadius: 4,
                  background: 'rgba(0,0,0,0.6)',
                  color: '#fff',
                  fontSize: 11,
                }}
              >
                {src.title}
              </div>
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: 18,
                  textAlign: 'center',
                  padding: '0 24px',
                }}
              >
                <span
                  style={{
                    display: 'inline-block',
                    padding: '8px 14px',
                    borderRadius: 8,
                    background: 'rgba(0,0,0,0.7)',
                    color: '#fff',
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                >
                  &ldquo;이 부분이 핵심이에요&rdquo;
                </span>
              </div>
            </div>

            <HighlightTimeline src={src} head={head} setHead={setHead} playing={playing} />
          </div>

          {/* Clips toolbar */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginTop: 8,
              flexWrap: 'wrap',
            }}
          >
            <h2 className="v36-h2" style={{ margin: 0, fontSize: 22 }}>
              추출된 숏폼 · {visibleClips.length}
            </h2>
            <span style={{ flex: 1 }} />
            <div className="v36-tabs" style={{ height: 36 }}>
              {(
                [
                  { id: 'all', label: '전체' },
                  { id: 'reels', label: '릴스 9:16' },
                  { id: 'tiktok', label: '틱톡' },
                  { id: 'yt', label: 'YT 쇼츠' },
                ] as const
              ).map((t) => (
                <button
                  key={t.id}
                  onClick={() => setFilter(t.id as 'all' | Platform)}
                  className={`v36-tab ${filter === t.id ? 'v36-tab--active' : ''}`}
                  style={{ height: 28, fontSize: 12 }}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Clips grid */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))',
              gap: 12,
            }}
          >
            {visibleClips.map((c) => (
              <ShortClipCard
                key={c.id}
                clip={c}
                active={selected === c.id}
                onSelect={() => setSelected(c.id)}
              />
            ))}
          </div>
        </div>

        {/* RIGHT detail panel */}
        <div
          className="v36-short-detail"
          style={{ display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0 }}
        >
          <ClipDetailPanel clip={sel} src={src} />
        </div>
      </div>

      <ShortCommandDock />

      <style>{`
        @media (max-width: 980px) {
          .v36-short-grid { grid-template-columns: 1fr !important; }
          .v36-short-detail { order: 2; }
        }
      `}</style>
    </main>
  )
}

/* ════════════════════════════════════════════════════════════ */
/* HighlightTimeline                                            */
/* ════════════════════════════════════════════════════════════ */
function HighlightTimeline({
  src,
  head,
  setHead,
  playing,
}: {
  src: Source
  head: number
  setHead: (v: number) => void
  playing: boolean
}) {
  const ref = useRef<HTMLDivElement>(null)

  const onClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const pct = (e.clientX - rect.left) / rect.width
    setHead(Math.max(0, Math.min(src.dur, pct * src.dur)))
  }

  const headPct = (head / src.dur) * 100

  return (
    <div>
      <div
        className="v36-mono"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          color: 'var(--v36-ink-4)',
          fontSize: 9,
          marginBottom: 6,
          letterSpacing: '0.08em',
        }}
      >
        <span>00:00</span>
        <span>{fmtClock(src.dur * 0.25)}</span>
        <span>{fmtClock(src.dur * 0.5)}</span>
        <span>{fmtClock(src.dur * 0.75)}</span>
        <span>{fmtClock(src.dur)}</span>
      </div>

      <div
        ref={ref}
        onClick={onClick}
        style={{
          position: 'relative',
          height: 72,
          cursor: 'pointer',
          background: 'rgba(255,255,255,0.04)',
          borderRadius: 8,
          border: '1px solid rgba(255,255,255,0.06)',
          overflow: 'hidden',
        }}
      >
        <svg
          width="100%"
          height="100%"
          preserveAspectRatio="none"
          viewBox="0 0 600 72"
          style={{ position: 'absolute', inset: 0 }}
        >
          {Array.from({ length: 200 }).map((_, i) => {
            const t = i / 200
            const seed = Math.sin(t * 41) * 0.5 + Math.sin(t * 13.7) * 0.3 + Math.cos(t * 7) * 0.2
            const inHL = src.highlights.some(
              (h) => t * src.dur >= h.start && t * src.dur <= h.start + h.dur
            )
            const h = (Math.abs(seed) * 0.7 + 0.15) * 56
            return (
              <rect
                key={i}
                x={i * 3}
                y={(72 - h) / 2}
                width={1.6}
                height={h}
                fill={inHL ? 'var(--v36-violet)' : 'rgba(255,255,255,0.22)'}
                rx={1}
              />
            )
          })}
        </svg>

        {/* Highlight pills */}
        {src.highlights.map((h, i) => {
          const left = (h.start / src.dur) * 100
          const width = (h.dur / src.dur) * 100
          return (
            <div
              key={i}
              title={h.label}
              style={{
                position: 'absolute',
                left: `${left}%`,
                top: 6,
                width: `${width}%`,
                height: 16,
                background: 'rgba(124,92,255,0.32)',
                border: '1px solid var(--v36-violet)',
                borderRadius: 999,
                display: 'flex',
                alignItems: 'center',
                padding: '0 6px',
                overflow: 'hidden',
              }}
            >
              <span
                className="v36-mono"
                style={{
                  fontSize: 8,
                  color: '#fff',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                ★ {h.score}
              </span>
            </div>
          )
        })}

        {src.highlights.map((h, i) => {
          const left = (h.start / src.dur) * 100
          return (
            <div
              key={`label-${i}`}
              style={{
                position: 'absolute',
                left: `${left}%`,
                bottom: 6,
                fontSize: 9,
                color: 'var(--v36-ink-3)',
                fontFamily: 'var(--v36-font-mono)',
                whiteSpace: 'nowrap',
              }}
            >
              {h.label}
            </div>
          )
        })}

        {/* Playhead */}
        <div
          aria-hidden
          style={{
            position: 'absolute',
            left: `${headPct}%`,
            top: 0,
            bottom: 0,
            width: 2,
            background: '#fff',
            boxShadow: '0 0 8px rgba(255,255,255,0.6)',
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: -5,
              top: -4,
              width: 12,
              height: 12,
              background: '#fff',
              borderRadius: '50%',
              boxShadow: '0 0 0 3px rgba(255,255,255,0.2)',
            }}
          />
        </div>
      </div>

      {/* Legend */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          marginTop: 10,
          flexWrap: 'wrap',
        }}
      >
        <span
          className="v36-mono"
          style={{
            fontSize: 9,
            color: 'var(--v36-ink-3)',
            textTransform: 'uppercase',
            letterSpacing: '0.10em',
          }}
        >
          {playing ? '▶ 재생 중' : '❚❚ 일시정지'}
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 10, height: 10, borderRadius: 3, background: 'var(--v36-violet)' }} />
          <span style={{ fontSize: 11, color: 'var(--v36-ink-3)' }}>AI 하이라이트</span>
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: 3,
              background: 'rgba(255,255,255,0.22)',
            }}
          />
          <span style={{ fontSize: 11, color: 'var(--v36-ink-3)' }}>오디오 파형</span>
        </span>
        <span style={{ flex: 1 }} />
        <button className="v36-btn v36-btn--ghost v36-btn--sm" style={{ height: 26, fontSize: 11 }}>
          <V3Icon name="sparkle" size={12} style={{ color: 'var(--v36-violet)' }} />
          <span>다시 분석</span>
        </button>
      </div>
    </div>
  )
}

/* ════════════════════════════════════════════════════════════ */
/* ShortClipCard                                                */
/* ════════════════════════════════════════════════════════════ */
function ShortClipCard({
  clip,
  active,
  onSelect,
}: {
  clip: ShortClip
  active: boolean
  onSelect: () => void
}) {
  return (
    <button
      onClick={onSelect}
      className="v36-card v36-card--hover"
      style={{
        padding: 6,
        textAlign: 'left',
        border: active ? '1.5px solid var(--v36-violet)' : '1px solid var(--v36-glass-border)',
        boxShadow: active ? '0 0 0 4px rgba(124,92,255,0.20)' : undefined,
        cursor: 'pointer',
      }}
    >
      <div
        className={`v36-thumb v36-thumb--${clip.tone}`}
        style={{ aspectRatio: '9/16', borderRadius: 8 }}
      >
        <div
          style={{
            position: 'absolute',
            top: 6,
            left: 6,
            padding: '2px 7px',
            borderRadius: 999,
            background: 'var(--v36-violet)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <V3Icon name="star" size={9} fill="#fff" stroke="#fff" />
          <span className="v36-mono" style={{ fontSize: 9, fontWeight: 600 }}>
            {clip.score}
          </span>
        </div>
        <div
          className="v36-mono"
          style={{
            position: 'absolute',
            right: 6,
            top: 6,
            padding: '2px 6px',
            borderRadius: 4,
            background: 'rgba(0,0,0,0.7)',
            color: '#fff',
            fontSize: 9,
          }}
        >
          {clip.dur}s
        </div>
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: 'rgba(0,0,0,0.5)',
            border: '1px solid rgba(255,255,255,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <V3Icon name="play" size={11} fill="#fff" />
        </div>
        <div
          style={{
            position: 'absolute',
            left: 6,
            right: 6,
            bottom: 8,
            textAlign: 'center',
          }}
        >
          <span
            style={{
              display: 'inline-block',
              padding: '3px 6px',
              borderRadius: 4,
              background: 'rgba(0,0,0,0.75)',
              color: '#fff',
              fontSize: 9,
              fontWeight: 600,
              lineHeight: 1.3,
              whiteSpace: 'pre-line',
            }}
          >
            {clip.caption}
          </span>
        </div>
        <div
          style={{
            position: 'absolute',
            left: 6,
            bottom: 6,
            padding: '1px 5px',
            borderRadius: 3,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            gap: 3,
          }}
        >
          <span
            style={{
              width: 5,
              height: 5,
              borderRadius: '50%',
              background: PLATFORM_DOT[clip.platform],
            }}
          />
          <span
            className="v36-mono"
            style={{
              fontSize: 8,
              color: '#fff',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}
          >
            {clip.platform}
          </span>
        </div>
      </div>
      <div style={{ padding: '8px 4px 2px' }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {clip.title}
        </div>
        <div
          className="v36-mono"
          style={{
            fontSize: 9,
            color: 'var(--v36-ink-4)',
            marginTop: 3,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
          }}
        >
          {fmtClock(clip.from)} → {fmtClock(clip.from + clip.dur)}
        </div>
      </div>
    </button>
  )
}

/* ════════════════════════════════════════════════════════════ */
/* ClipDetailPanel                                              */
/* ════════════════════════════════════════════════════════════ */
function ClipDetailPanel({ clip, src }: { clip: ShortClip | null; src: Source }) {
  const router = useRouter()
  if (!clip) {
    return (
      <div
        className="v36-glass"
        style={{
          padding: 24,
          borderRadius: 16,
          textAlign: 'center',
          color: 'var(--v36-ink-4)',
          fontSize: 13,
        }}
      >
        클립을 선택하세요
      </div>
    )
  }
  return (
    <>
      {/* preview */}
      <div className="v36-glass" style={{ padding: 12, borderRadius: 14 }}>
        <div
          className={`v36-thumb v36-thumb--${clip.tone}`}
          style={{
            aspectRatio: '9/16',
            maxHeight: 360,
            width: '100%',
            borderRadius: 10,
            margin: '0 auto',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 52,
              height: 52,
              borderRadius: '50%',
              background: 'rgba(0,0,0,0.55)',
              border: '1px solid rgba(255,255,255,0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <V3Icon name="play" size={18} fill="#fff" />
          </div>
          <div style={{ position: 'absolute', left: 0, right: 0, bottom: 16, textAlign: 'center' }}>
            <span
              style={{
                display: 'inline-block',
                padding: '8px 12px',
                borderRadius: 6,
                background: 'rgba(0,0,0,0.75)',
                color: '#fff',
                fontSize: 13,
                fontWeight: 600,
                whiteSpace: 'pre-line',
              }}
            >
              {clip.caption}
            </span>
          </div>
          <div
            className="v36-mono"
            style={{
              position: 'absolute',
              right: 8,
              top: 8,
              padding: '3px 7px',
              borderRadius: 999,
              background: 'rgba(0,0,0,0.7)',
              color: '#fff',
              fontSize: 10,
            }}
          >
            9:16 · {clip.dur}s
          </div>
        </div>
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>{clip.title}</div>
          <div
            className="v36-mono"
            style={{
              fontSize: 10,
              color: 'var(--v36-ink-4)',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}
          >
            FROM {src.title} · {fmtClock(clip.from)}
          </div>
        </div>
      </div>

      {/* score breakdown */}
      <div className="v36-glass" style={{ padding: 14, borderRadius: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <V3Icon name="sparkle" size={13} style={{ color: 'var(--v36-violet)' }} />
          <span className="v36-label" style={{ fontSize: 9 }}>
            AI 점수 · {clip.score} / 100
          </span>
        </div>
        {clip.signals.map((s, i) => (
          <div key={i} style={{ marginBottom: 10 }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 11,
                marginBottom: 4,
              }}
            >
              <span style={{ color: 'var(--v36-ink-2)' }}>{s.label}</span>
              <span className="v36-mono" style={{ color: 'var(--v36-ink-3)' }}>
                {s.value}
              </span>
            </div>
            <div
              style={{
                height: 4,
                background: 'rgba(255,255,255,0.06)',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${s.value}%`,
                  height: '100%',
                  background:
                    i === 0
                      ? 'var(--v36-violet)'
                      : i === 1
                        ? 'var(--v36-cyan)'
                        : 'var(--v36-lime)',
                  borderRadius: 2,
                }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* caption + hashtag */}
      <div className="v36-glass" style={{ padding: 14, borderRadius: 14 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 10,
          }}
        >
          <span className="v36-label" style={{ fontSize: 9 }}>
            SNS 캡션
          </span>
          <button
            className="v36-btn v36-btn--ghost"
            style={{ height: 22, padding: '0 8px', fontSize: 10 }}
          >
            <V3Icon name="sparkle" size={10} />
            <span>다시 쓰기</span>
          </button>
        </div>
        <div
          style={{
            fontSize: 13,
            lineHeight: 1.55,
            color: 'var(--v36-ink)',
            marginBottom: 10,
          }}
        >
          {clip.snsCaption}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {clip.hashtags.map((h) => (
            <span
              key={h}
              className="v36-chip"
              style={{
                height: 24,
                fontSize: 11,
                color: 'var(--v36-violet)',
                background: 'rgba(124,92,255,0.10)',
                borderColor: 'rgba(124,92,255,0.30)',
              }}
            >
              #{h}
            </span>
          ))}
        </div>
      </div>

      {/* publish */}
      <div className="v36-glass" style={{ padding: 14, borderRadius: 14 }}>
        <span className="v36-label" style={{ fontSize: 9, marginBottom: 10, display: 'block' }}>
          게시
        </span>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 6,
            marginBottom: 8,
          }}
        >
          {[
            { id: 'ig', label: 'Instagram', color: '#FF6FA8' },
            { id: 'tt', label: 'TikTok', color: '#00D4E0' },
            { id: 'yt', label: 'YouTube', color: '#FF6B5B' },
            { id: 'x', label: 'X / Threads', color: '#C8FF00' },
          ].map((p) => (
            <button
              key={p.id}
              className="v36-btn v36-btn--sm"
              style={{ height: 34, justifyContent: 'flex-start', fontSize: 12 }}
            >
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color }} />
              <span>{p.label}</span>
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            className="v36-btn v36-btn--sm"
            onClick={() => router.push('/projects')}
            style={{ flex: 1, justifyContent: 'center' }}
          >
            <V3Icon name="film" size={13} />
            <span>편집</span>
          </button>
          <button
            className="v36-btn v36-btn--neon v36-btn--sm"
            style={{ flex: 1, justifyContent: 'center' }}
          >
            <V3Icon name="download" size={13} />
            <span>저장</span>
          </button>
        </div>
      </div>
    </>
  )
}

/* ════════════════════════════════════════════════════════════ */
/* ShortCommandDock                                             */
/* ════════════════════════════════════════════════════════════ */
function ShortCommandDock() {
  const router = useRouter()
  const { createYouTubeHighlight } = useMakerStore()
  const [mode, setMode] = useState<'url' | 'upload'>('url')
  const [url, setUrl] = useState('')
  const [exp, setExp] = useState(false)
  const [count, setCount] = useState(8)
  const [length, setLength] = useState('30s')
  const [busy, setBusy] = useState(false)

  const onGenerate = async () => {
    if (mode === 'url' && url.trim()) {
      setBusy(true)
      try {
        const result = await createYouTubeHighlight({
          youtubeUrl: url.trim(),
          targetCount: count,
          targetDuration: parseInt(length.replace('s', ''), 10) || 30,
          language: 'ko',
          addCaptions: true,
        })
        router.push(`/youtube-highlight/${result.projectId}`)
      } catch (e) {
        console.warn('createYouTubeHighlight failed', e)
      } finally {
        setBusy(false)
      }
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 'min(820px, calc(100% - 24px))',
        zIndex: 40,
      }}
    >
      {exp && (
        <div
          className="v36-glass-strong v36-fade-in"
          style={{
            marginBottom: 8,
            padding: 14,
            borderRadius: 18,
            boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 12,
              flexWrap: 'wrap',
            }}
          >
            <span className="v36-label" style={{ fontSize: 9 }}>
              개수
            </span>
            <div style={{ display: 'flex', gap: 4 }}>
              {[3, 5, 8, 12].map((n) => (
                <button
                  key={n}
                  onClick={() => setCount(n)}
                  className={`v36-chip v36-mono ${count === n ? 'v36-chip--active' : ''}`}
                  style={{ height: 26, fontSize: 11 }}
                >
                  {n}개
                </button>
              ))}
            </div>
            <span style={{ width: 1, height: 16, background: 'var(--v36-line)' }} />
            <span className="v36-label" style={{ fontSize: 9 }}>
              길이
            </span>
            <div style={{ display: 'flex', gap: 4 }}>
              {['15s', '30s', '60s', 'AUTO'].map((t) => (
                <button
                  key={t}
                  onClick={() => setLength(t)}
                  className={`v36-chip v36-mono ${length === t ? 'v36-chip--active' : ''}`}
                  style={{ height: 26, fontSize: 11 }}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <span
              className="v36-label"
              style={{ fontSize: 9, alignSelf: 'center', marginRight: 4 }}
            >
              포함
            </span>
            {['자동 자막', '비트 싱크', '수직 크롭', '후크 강조', '트렌드 BGM'].map((t) => (
              <button key={t} className="v36-chip" style={{ height: 26, fontSize: 11 }}>
                <V3Icon name="check" size={10} />
                <span>{t}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div
        className="v36-glass-strong"
        style={{
          borderRadius: 24,
          padding: 6,
          boxShadow: '0 24px 64px rgba(0,0,0,0.5), 0 0 0 1px rgba(124,92,255,0.2)',
          border: '1px solid rgba(255,255,255,0.14)',
        }}
      >
        <div style={{ display: 'flex', gap: 2, padding: '4px 4px 0' }}>
          <button
            onClick={() => setMode('url')}
            className={`v36-tab ${mode === 'url' ? 'v36-tab--active' : ''}`}
            style={{ height: 28, fontSize: 11 }}
          >
            <V3Icon name="share" size={11} />
            <span>URL</span>
          </button>
          <button
            onClick={() => setMode('upload')}
            className={`v36-tab ${mode === 'upload' ? 'v36-tab--active' : ''}`}
            style={{ height: 28, fontSize: 11 }}
          >
            <V3Icon name="upload" size={11} />
            <span>파일 업로드</span>
          </button>
          <span style={{ flex: 1 }} />
          <span
            className="v36-mono"
            style={{
              fontSize: 10,
              color: 'var(--v36-ink-4)',
              alignSelf: 'center',
              paddingRight: 8,
            }}
          >
            {count}개 · {length}
          </span>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: '0 4px 0 14px',
          }}
        >
          {mode === 'url' ? (
            <>
              <V3Icon name="share" size={16} style={{ color: 'var(--v36-violet)', flexShrink: 0 }} />
              <input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="YouTube · Vimeo · 직접 링크 붙여넣기…"
                style={{
                  flex: 1,
                  minWidth: 0,
                  height: 44,
                  border: 0,
                  background: 'transparent',
                  fontSize: 14,
                  outline: 'none',
                  color: '#fff',
                }}
              />
            </>
          ) : (
            <>
              <V3Icon name="upload" size={16} style={{ color: 'var(--v36-violet)', flexShrink: 0 }} />
              <span
                style={{
                  flex: 1,
                  height: 44,
                  display: 'flex',
                  alignItems: 'center',
                  color: 'var(--v36-ink-3)',
                  fontSize: 14,
                }}
              >
                MP4, MOV, MKV — 최대 4GB · 드래그 앤 드롭
              </span>
            </>
          )}
          <button
            className="v36-btn v36-btn--ghost v36-btn--sm"
            onClick={() => setExp(!exp)}
            aria-label="옵션"
            style={{
              width: 36,
              padding: 0,
              color: exp ? 'var(--v36-violet)' : 'var(--v36-ink-3)',
            }}
          >
            <V3Icon name={exp ? 'chevD' : 'settings'} size={16} />
          </button>
          <button
            className="v36-btn v36-btn--neon"
            onClick={onGenerate}
            disabled={busy || (mode === 'url' && !url.trim())}
            style={{
              height: 36,
              padding: '0 16px',
              opacity: busy || (mode === 'url' && !url.trim()) ? 0.5 : 1,
            }}
          >
            <V3Icon name="bolt" size={14} />
            <span>{busy ? '분석 중…' : '분석 시작'}</span>
          </button>
        </div>
      </div>

      {!exp && (
        <div
          style={{
            display: 'flex',
            gap: 6,
            justifyContent: 'center',
            marginTop: 8,
            flexWrap: 'wrap',
          }}
        >
          {['최근 업로드 5개', '유튜브 라이브에서', '30초로 통일', '후크만 강조', '영문 자막'].map(
            (s) => (
              <button
                key={s}
                className="v36-chip"
                style={{ height: 26, fontSize: 12, background: 'rgba(0,0,0,0.4)' }}
              >
                {s}
              </button>
            )
          )}
        </div>
      )}
    </div>
  )
}
